import React, { useState, useEffect } from 'react';
import { Doctor } from '../../types';
import api from '../../services/api';
import './DoctorForm.css';

interface DoctorFormProps {
  doctor: Doctor | null;
  onClose: () => void;
  onSubmit: () => void;
}

const DoctorForm: React.FC<DoctorFormProps> = ({ doctor, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    specialization: '',
    schedule: '',
    is_available: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (doctor) {
      setFormData({
        name: doctor.name,
        specialization: doctor.specialization,
        schedule: doctor.schedule,
        is_available: doctor.is_available
      });
    }
  }, [doctor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.specialization || !formData.schedule) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      let response;

      if (doctor) {
        response = await api.put(`/admin/doctors/${doctor.id}`, formData);
      } else {
        response = await api.post('/admin/doctors', formData);
      }

      if (response.data.success) {
        onSubmit();
      } else {
        setError(response.data.message || 'Failed to save doctor');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving doctor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{doctor ? 'Edit Doctor' : 'Add New Doctor'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="name">Doctor Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Dr. David Smith"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="specialization">Specialization *</label>
              <input
                type="text"
                id="specialization"
                name="specialization"
                value={formData.specialization}
                onChange={handleChange}
                placeholder="e.g., Veterinary Surgeon"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="schedule">Schedule *</label>
              <input
                type="text"
                id="schedule"
                name="schedule"
                value={formData.schedule}
                onChange={handleChange}
                placeholder="e.g., Mon-Fri 9 AM - 5 PM"
                required
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_available"
                  checked={formData.is_available}
                  onChange={handleChange}
                />
                <span>Doctor is currently available</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Saving...' : doctor ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoctorForm;